// ============================================================
// WISHLIST — Drawer + Share support
// All wishlist elements use the prefix  "wishlist-"  so they
// never collide with Shopify Horizon's cart-drawer elements.
// Display mode is read from window.wishlistConfig.displayMode
// ('page' | 'drawer') injected via theme.liquid.
// ============================================================

// ── Helper: get or create a dedicated portal div that is a
//    direct child of <body>. Using display:contents means the
//    portal itself produces no box and cannot create a new
//    stacking context, so its children (drawer, overlay, modal)
//    stack against the root stacking context only.
function getOrCreatePortal() {
  let portal = document.getElementById("wishlist-portal")
  if (!portal) {
    portal = document.createElement("div")
    portal.id = "wishlist-portal"
    portal.style.cssText = "position:static;display:contents;"
    document.body.appendChild(portal)
  }
  return portal
}

// ============================================================
// WISHLIST SHARE
// Encodes wishlist items into the URL as a base64 payload so
// recipients see a read-only snapshot on the wishlist page.
// ============================================================
class WishlistShare {
  constructor(wishlist) {
    this.wishlist = wishlist
    this.modal = null
    this.modalOverlay = null
    this._createModal()
    this._bindGlobalTriggers()
  }

  // ── Build the shareable URL ──────────────────────────────
  buildShareUrl() {
    const items = this.wishlist.items
    if (!items.length) return window.location.origin + "/pages/wishlist"
    try {
      const payload = btoa(unescape(encodeURIComponent(JSON.stringify(items))))
      return (
        window.location.origin +
        "/pages/wishlist?shared=" +
        encodeURIComponent(payload)
      )
    } catch {
      return window.location.origin + "/pages/wishlist"
    }
  }

  // ── Decode a shared payload from the URL ────────────────
  static decodeSharedPayload(raw) {
    try {
      return JSON.parse(decodeURIComponent(escape(atob(decodeURIComponent(raw)))))
    } catch {
      return null
    }
  }

  // ── Copy URL to clipboard ────────────────────────────────
  async copyLink(btn) {
    const url = this.buildShareUrl()
    try {
      await navigator.clipboard.writeText(url)
    } catch {
      const ta = document.createElement("textarea")
      ta.value = url
      ta.style.cssText = "position:fixed;opacity:0"
      document.body.appendChild(ta)
      ta.select()
      document.execCommand("copy")
      ta.remove()
    }
    if (btn) {
      const original = btn.innerHTML
      btn.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg> Copied!`
      btn.classList.add("is-copied")
      setTimeout(() => {
        btn.innerHTML = original
        btn.classList.remove("is-copied")
      }, 2200)
    }
  }

  // ── Share channel definitions ────────────────────────────
  _channels(url) {
    const text = encodeURIComponent("Check out my wishlist! \uD83D\uDECD\uFE0F")
    const enc  = encodeURIComponent(url)
    return [
      {
        key: "whatsapp", label: "WhatsApp",
        icon: `<svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>`,
        href: `https://wa.me/?text=${text}%20${enc}`,
      },
      {
        key: "facebook", label: "Facebook",
        icon: `<svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>`,
        href: `https://www.facebook.com/sharer/sharer.php?u=${enc}`,
      },
      {
        key: "twitter", label: "X / Twitter",
        icon: `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.746l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>`,
        href: `https://twitter.com/intent/tweet?text=${text}&url=${enc}`,
      },
      {
        key: "email", label: "Email",
        icon: `<svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>`,
        href: `mailto:?subject=My%20Wishlist&body=${text}%20${enc}`,
      },
    ]
  }

  // ── Create the share modal once, teleport to portal ──────
  _createModal() {
    const portal = getOrCreatePortal()

    const overlayEl = document.createElement("div")
    overlayEl.id = "wishlist-share-modal-overlay"
    overlayEl.className = "wishlist-share-modal-overlay"
    overlayEl.setAttribute("aria-hidden", "true")
    portal.appendChild(overlayEl)

    const modalEl = document.createElement("div")
    modalEl.id = "wishlist-share-modal"
    modalEl.className = "wishlist-share-modal"
    modalEl.setAttribute("role", "dialog")
    modalEl.setAttribute("aria-modal", "true")
    modalEl.setAttribute("aria-labelledby", "wishlist-share-modal-title")
    modalEl.setAttribute("aria-hidden", "true")
    portal.appendChild(modalEl)

    this.modal        = modalEl
    this.modalOverlay = overlayEl

    overlayEl.addEventListener("click", () => this.closeModal())
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && this.modal.classList.contains("is-open")) {
        this.closeModal()
      }
    })
  }

  // ── Render modal content fresh on every open ─────────────
  _renderModal() {
    const url      = this.buildShareUrl()
    const channels = this._channels(url)

    this.modal.innerHTML = `
      <div class="wishlist-share-modal__handle" aria-hidden="true"></div>
      <div class="wishlist-share-modal__header">
        <h3 class="wishlist-share-modal__title" id="wishlist-share-modal-title">Share Wishlist</h3>
        <button class="wishlist-share-modal__close" id="wishlist-share-modal-close" type="button" aria-label="Close">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>
      </div>
      <div class="wishlist-share-modal__url-row">
        <input
          class="wishlist-share-modal__url-input"
          type="text"
          readonly
          value="${url}"
          aria-label="Shareable wishlist link"
        />
        <button class="wishlist-share-modal__copy-btn" id="wishlist-share-copy-btn" type="button">
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
          </svg>
          Copy link
        </button>
      </div>
      <div class="wishlist-share-modal__channels">
        ${channels.map((ch) => `
          <a
            href="${ch.href}"
            target="_blank"
            rel="noopener noreferrer"
            class="wishlist-share-channel wishlist-share-channel--${ch.key}"
            aria-label="Share on ${ch.label}"
          >
            <span class="wishlist-share-channel__icon">${ch.icon}</span>
            <span>${ch.label}</span>
          </a>`).join("")}
      </div>`

    this.modal
      .querySelector("#wishlist-share-modal-close")
      .addEventListener("click", () => this.closeModal())

    const copyBtn = this.modal.querySelector("#wishlist-share-copy-btn")
    copyBtn.addEventListener("click", () => this.copyLink(copyBtn))
  }

  openModal() {
    this._renderModal()
    this.modal.classList.add("is-open")
    this.modalOverlay.classList.add("is-open")
    this.modal.setAttribute("aria-hidden", "false")
    this.modalOverlay.setAttribute("aria-hidden", "false")
  }

  closeModal() {
    this.modal.classList.remove("is-open")
    this.modalOverlay.classList.remove("is-open")
    this.modal.setAttribute("aria-hidden", "true")
    this.modalOverlay.setAttribute("aria-hidden", "true")
  }

  // ── Any element with data-wishlist-share-trigger opens modal
  _bindGlobalTriggers() {
    document.addEventListener("click", (e) => {
      if (e.target.closest("[data-wishlist-share-trigger]")) {
        e.preventDefault()
        this.openModal()
      }
    })
  }
}

// ============================================================
// WISHLIST DRAWER
// ============================================================
class WishlistDrawer {
  constructor(wishlist) {
    this.wishlist  = wishlist
    this.drawer    = document.getElementById("wishlist-drawer")
    this.overlay   = document.getElementById("wishlist-drawer-overlay")
    this.closeBtn  = document.getElementById("wishlist-drawer-close")
    this.itemsList = document.getElementById("wishlist-drawer-items")
    this.emptyState= document.getElementById("wishlist-drawer-empty")
    this.countBadge= document.querySelector("[data-wishlist-drawer-count]")
    this.template  = document.getElementById("wishlist-drawer-item-template")
    this.footer    = document.getElementById("wishlist-drawer-footer")

    if (!this.drawer) return

    // Move drawer + overlay into the portal so they are direct
    // children of <body> and escape every ancestor stacking context.
    // This does NOT touch any cart-drawer elements.
    const portal = getOrCreatePortal()
    portal.appendChild(this.overlay)
    portal.appendChild(this.drawer)

    this._bindEvents()
    this.render()
  }

  _bindEvents() {
    if (this.closeBtn)  this.closeBtn.addEventListener("click",  () => this.close())
    if (this.overlay)   this.overlay.addEventListener("click",   () => this.close())

    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && this.isOpen()) this.close()
    })

    // ── Single delegated listener — catches every trigger click
    //    regardless of which theme, which snippet, or when the
    //    element is stamped into the DOM (works with custom
    //    elements, web components, async hydration, etc.).
    //
    //    Matches:
    //      [data-wishlist-header-trigger]   — our own button/link
    //      [data-wishlist-drawer-trigger]   — any alias
    //      #wishlist-drawer-trigger         — legacy id fallback
    //      #bottom-nav-wishlist-trigger     — bottom nav button
    //      header a[href="/pages/wishlist"] — Tokyo / Dawn / any
    //      nav    a[href="/pages/wishlist"]   theme that links to
    //      [role="banner"] a[href=...]        the wishlist page
    document.addEventListener("click", (e) => {
      const t = e.target

      // Check the clicked element and all its ancestors up to <body>
      const trigger = t.closest(
        '[data-wishlist-header-trigger], ' +
        '[data-wishlist-drawer-trigger], ' +
        '#wishlist-drawer-trigger, ' +
        '#bottom-nav-wishlist-trigger'
      )
      if (trigger) {
        e.preventDefault()
        e.stopPropagation()
        this.open()
        return
      }

      // Fallback: any wishlist page link inside the site header area
      const wishlistLink = t.closest('a[href="/pages/wishlist"]')
      if (
        wishlistLink &&
        wishlistLink.closest('header, nav, [role="banner"], #shopify-section-header, .site-header, .header-wrapper, .site-nav')
      ) {
        e.preventDefault()
        e.stopPropagation()
        this.open()
      }
    })

    // Delegated events inside the items list
    if (this.itemsList) {
      this.itemsList.addEventListener("click", (e) => {
        const removeBtn = e.target.closest(".wishlist-drawer__remove")
        if (removeBtn) {
          const productId = removeBtn.dataset.productId
          const item = removeBtn.closest(".wishlist-drawer__item")
          if (item) item.classList.add("removing")
          setTimeout(() => {
            this.wishlist.removeFromWishlist(productId)
            this.render()
          }, 300)
          return
        }

        const atcBtn = e.target.closest(".wishlist-drawer__atc")
        if (atcBtn) {
          this.wishlist.addToCart(atcBtn.dataset.variantId, atcBtn.dataset.productId, atcBtn)
        }
      })
    }

    document.addEventListener("wishlist:updated", () => this.render())
  }

  open() {
    if (!this.drawer) return
    this.render()
    this.drawer.classList.add("is-open")
    if (this.overlay) this.overlay.classList.add("is-open")
    document.body.style.overflow = "hidden"
    this.drawer.setAttribute("aria-hidden", "false")
    setTimeout(() => { if (this.closeBtn) this.closeBtn.focus() }, 50)
  }

  close() {
    if (!this.drawer) return
    this.drawer.classList.remove("is-open")
    if (this.overlay) this.overlay.classList.remove("is-open")
    document.body.style.overflow = ""
    this.drawer.setAttribute("aria-hidden", "true")
  }

  isOpen() {
    return this.drawer && this.drawer.classList.contains("is-open")
  }

  render() {
    if (!this.drawer || !this.template) return

    const items = this.wishlist.items
    const count = items.length

    if (this.countBadge) {
      this.countBadge.textContent = count > 0 ? count : ""
    }

    // Show footer share button only when there are items
    if (this.footer) {
      this.footer.classList.toggle("is-hidden", count === 0)
    }

    if (count === 0) {
      if (this.itemsList)  this.itemsList.innerHTML = ""
      if (this.emptyState) this.emptyState.classList.remove("is-hidden")
      return
    }

    if (this.emptyState) this.emptyState.classList.add("is-hidden")

    const tpl = this.template.innerHTML
    this.itemsList.innerHTML = items.map((item) =>
      tpl
        .replace(/\[\[id\]\]/g,         item.id)
        .replace(/\[\[title\]\]/g,      item.title)
        .replace(/\[\[image\]\]/g,      item.image || "")
        .replace(/\[\[url\]\]/g,        item.url)
        .replace(/\[\[price\]\]/g,      item.price)
        .replace(/\[\[variant_id\]\]/g, item.variant_id)
    ).join("")
  }
}

// ============================================================
// MAIN WISHLIST CLASS
// ============================================================
class Wishlist {
  constructor() {
    this.storageKey = "shopify_wishlist"
    this.items      = this.getWishlist()

    this.selectors = {
      wishlistButton:   "[data-wishlist-button]",
      wishlistCounter:  "[data-wishlist-counter]",
      wishlistPage:     ".wishlist-container",
      wishlistItems:    "#wishlist-items",
      wishlistEmpty:    "#wishlist-empty",
      addToCartButtons: ".wishlist-add-to-cart",
      removeButtons:    ".wishlist-remove",
      notification:        "#wishlist-notification",
      notificationImage:   "#wishlist-notification-image",
      notificationMessage: "#wishlist-notification-message",
    }

    this.classes = {
      active:  "is-active",
      loading: "is-loading",
      hidden:  "is-hidden",
      added:   "is-added",
    }

    this.displayMode =
      (window.wishlistConfig && window.wishlistConfig.displayMode) || "page"

    this.init()
    this.initOnPageLoad()
    this.createNotificationElement()

    // Shared wishlist: decode ?shared= param before initialising share/drawer
    this._handleSharedView()

    // Share feature works in both page and drawer modes
    this.share = new WishlistShare(this)
    window.wishlistShare = this.share

    // Drawer
    if (this.displayMode === "drawer") {
      this.drawer = new WishlistDrawer(this)
      window.wishlistDrawer = this.drawer
    }
  }

  // ── Shared wishlist read-only view ───────────────────────
  _handleSharedView() {
    const params = new URLSearchParams(window.location.search)
    const shared = params.get("shared")
    if (!shared) return

    const decoded = WishlistShare.decodeSharedPayload(shared)
    if (!decoded || !decoded.length) return

    this._sharedItems   = decoded
    this._isSharedView  = true

    // Reveal the "viewing someone's wishlist" banner
    const banner = document.getElementById("wishlist-shared-banner")
    if (banner) banner.classList.remove("is-hidden")

    // Hide the share bar — you can't re-share someone else's list
    const shareBar = document.getElementById("wishlist-share-bar")
    if (shareBar) shareBar.classList.add("is-hidden")
  }

  createNotificationElement() {
    if (document.querySelector(this.selectors.notification)) return
    const el = document.createElement("div")
    el.id        = "wishlist-notification"
    el.className = "wishlist-notification"
    el.innerHTML = `
      <div class="wishlist-notification-content">
        <img id="wishlist-notification-image" src="" alt="">
        <p id="wishlist-notification-message"></p>
      </div>`
    document.body.appendChild(el)
  }

  observeCounterChanges() {
    document.querySelectorAll(this.selectors.wishlistCounter).forEach((counter) => {
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.attributeName === "class") {
            const count = this.items.length
            if (count > 0 && counter.classList.contains(this.classes.hidden)) {
              counter.classList.remove(this.classes.hidden)
              const span = counter.querySelector("span[aria-hidden]")
              if (span) span.textContent = count
              else counter.textContent = count
            }
          }
        })
      })
      observer.observe(counter, { attributes: true, attributeFilter: ["class"] })
    })
  }

  initOnPageLoad() {
    const isWishlistPage = window.location.pathname.includes("/pages/wishlist")

    setTimeout(() => {
      this.updateWishlistButtons()

      if (isWishlistPage) {
        if (this._isSharedView && this._sharedItems) {
          this._renderSharedPage()
        } else {
          this.updateWishlistPage()
        }
      }

      this.updateWishlistCounter()
      this.observeCounterChanges()
    }, 100)
  }

  _renderSharedPage() {
    const wishlistItems = document.querySelector(this.selectors.wishlistItems)
    const wishlistEmpty = document.querySelector(this.selectors.wishlistEmpty)
    if (!wishlistItems || !wishlistEmpty) return

    const items = this._sharedItems
    if (!items.length) {
      wishlistEmpty.classList.remove(this.classes.hidden)
      return
    }
    wishlistEmpty.classList.add(this.classes.hidden)

    const template = document.getElementById("wishlist-item-template")
    if (!template) return

    wishlistItems.innerHTML = items.map((item) =>
      template.innerHTML
        .replace(/\[\[id\]\]/g,         item.id)
        .replace(/\[\[title\]\]/g,      item.title)
        .replace(/\[\[image\]\]/g,      item.image || "")
        .replace(/\[\[url\]\]/g,        item.url)
        .replace(/\[\[price\]\]/g,      item.price)
        .replace(/\[\[variant_id\]\]/g, item.variant_id)
    ).join("")
  }

  init() {
    this.initWishlistButtons()
    this.initWishlistPage()
    this.updateWishlistCounter()
    this.updateWishlistButtons()

    window.addEventListener("storage", (event) => {
      if (event.key === this.storageKey) {
        this.items = JSON.parse(event.newValue || "[]")
        this.updateWishlistButtons()
        this.updateWishlistCounter()
        this.updateWishlistPage()
      }
    })
  }

  getWishlist() {
    const data = localStorage.getItem(this.storageKey)
    return data ? JSON.parse(data) : []
  }

  saveWishlist() {
    localStorage.setItem(this.storageKey, JSON.stringify(this.items))
    this.updateWishlistCounter()
    this.updateWishlistButtons()
    document.dispatchEvent(
      new CustomEvent("wishlist:updated", { detail: { items: this.items } })
    )
  }

  updateWishlistCounter() {
    document.querySelectorAll(this.selectors.wishlistCounter).forEach((counter) => {
      const count = this.items.length
      const span  = counter.querySelector("span[aria-hidden]")
      if (span) span.textContent = count
      else counter.textContent = count

      if (count > 0) counter.classList.remove(this.classes.hidden)
      else           counter.classList.add(this.classes.hidden)
    })
  }

  initWishlistButtons() {
    document.querySelectorAll(this.selectors.wishlistButton).forEach((button) => {
      if (this.isInWishlist(button.dataset.productId)) {
        button.classList.add(this.classes.added)
      }
      button.addEventListener("click", (event) => {
        event.preventDefault()
        event.stopPropagation()
        this.toggleWishlistItem(button)
      })
    })
  }

  updateWishlistButtons() {
    document.querySelectorAll(this.selectors.wishlistButton).forEach((button) => {
      const inList   = this.isInWishlist(button.dataset.productId)
      const heartIcon = button.querySelector(".icon-heart")
      button.classList.toggle(this.classes.added, inList)
      if (heartIcon) heartIcon.setAttribute("fill", inList ? "currentColor" : "none")
    })
  }

  toggleWishlistItem(button) {
    const productId    = button.dataset.productId
    const productTitle = button.dataset.productTitle
    const productImage = button.dataset.productImage
    const productUrl   = button.dataset.productUrl
    const productPrice = button.dataset.productPrice
    const variantId    = button.dataset.variantId
    const heartIcon    = button.querySelector(".icon-heart")

    if (this.isInWishlist(productId)) {
      this.removeFromWishlist(productId)
      button.classList.remove(this.classes.added)
      if (heartIcon) heartIcon.setAttribute("fill", "none")
      this.showNotification(productImage, `${productTitle} removed from wishlist`)
    } else {
      this.addToWishlist({ id: productId, title: productTitle, image: productImage,
                           url: productUrl, price: productPrice, variant_id: variantId })
      button.classList.add(this.classes.added)
      if (heartIcon) heartIcon.setAttribute("fill", "currentColor")
      this.showNotification(productImage, `${productTitle} added to wishlist`)

      if (this.displayMode === "drawer" && this.drawer) {
        setTimeout(() => this.drawer.open(), 400)
      }
    }
  }

  isInWishlist(productId) {
    return this.items.some((item) => item.id === productId)
  }

  addToWishlist(product) {
    if (!this.isInWishlist(product.id)) {
      this.items.push(product)
      this.saveWishlist()
    }
  }

  removeFromWishlist(productId) {
    this.items = this.items.filter((item) => item.id !== productId)
    this.saveWishlist()
    this.updateWishlistPage()
  }

  initWishlistPage() {
    const wishlistPage = document.querySelector(this.selectors.wishlistPage)
    if (!wishlistPage) return

    this.updateWishlistPage()

    wishlistPage.addEventListener("click", (event) => {
      const target = event.target

      if (target.closest(this.selectors.removeButtons)) {
        const button    = target.closest(this.selectors.removeButtons)
        const productId = button.dataset.productId
        const item      = button.closest(".wishlist-item")
        if (item) item.classList.add("removing")
        setTimeout(() => this.removeFromWishlist(productId), 300)
      }

      if (target.closest(this.selectors.addToCartButtons)) {
        const button = target.closest(this.selectors.addToCartButtons)
        this.addToCart(button.dataset.variantId, button.dataset.productId, button)
      }
    })
  }

  updateWishlistPage() {
    // Never overwrite a shared read-only view
    if (this._isSharedView) return

    const wishlistItems = document.querySelector(this.selectors.wishlistItems)
    const wishlistEmpty = document.querySelector(this.selectors.wishlistEmpty)
    if (!wishlistItems || !wishlistEmpty) return

    const shareBar = document.getElementById("wishlist-share-bar")

    if (this.items.length === 0) {
      wishlistItems.innerHTML = ""
      wishlistEmpty.classList.remove(this.classes.hidden)
      if (shareBar) shareBar.classList.add("is-hidden")
      return
    }

    wishlistEmpty.classList.add(this.classes.hidden)
    if (shareBar) shareBar.classList.remove("is-hidden")

    const template = document.getElementById("wishlist-item-template")
    if (!template) return

    wishlistItems.innerHTML = this.items.map((item) =>
      template.innerHTML
        .replace(/\[\[id\]\]/g,         item.id)
        .replace(/\[\[title\]\]/g,      item.title)
        .replace(/\[\[image\]\]/g,      item.image || "")
        .replace(/\[\[url\]\]/g,        item.url)
        .replace(/\[\[price\]\]/g,      item.price)
        .replace(/\[\[variant_id\]\]/g, item.variant_id)
    ).join("")
  }

  async addToCart(variantId, productId, button) {
    if (!variantId) return
    button.classList.add(this.classes.loading)

    try {
      const response = await fetch("/cart/add.js", {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ id: variantId, quantity: 1 }),
      })
      if (!response.ok) throw new Error("Failed to add to cart")

      const product = this.items.find((item) => item.id === productId)
      if (product) {
        this.showNotification(product.image, `${product.title} added to cart`)
        this.removeFromWishlist(productId)
      }

      if (typeof window.updateCartDrawer === "function") {
        window.updateCartDrawer()
      } else {
        window.location.href = "/cart"
      }
    } catch (error) {
      console.error("Error adding to cart:", error)
      alert("Could not add to cart. Please try again.")
    } finally {
      button.classList.remove(this.classes.loading)
    }
  }

  showNotification(image, message) {
    const notification       = document.querySelector(this.selectors.notification)
    const notificationImage  = document.querySelector(this.selectors.notificationImage)
    const notificationMessage= document.querySelector(this.selectors.notificationMessage)
    if (!notification || !notificationImage || !notificationMessage) return

    notificationImage.src        = image || ""
    notificationMessage.textContent = message
    notification.classList.add(this.classes.active)

    clearTimeout(this.notificationTimeout)
    this.notificationTimeout = setTimeout(() => {
      notification.classList.remove(this.classes.active)
    }, 3000)
  }
}

document.addEventListener("DOMContentLoaded", () => {
  window.wishlist = new Wishlist()
})
